import React from 'react';
import { Link } from 'react-router-dom';
import { MarketingLayout } from '../layouts/MarketingLayout';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  BarChart3, 
  Shield, 
  Upload, 
  Calculator,
  TrendingUp,
  AlertTriangle,
  FileText,
  CheckCircle,
  FileBarChart,
  MapPin,
  Lock,
  Cpu,
} from 'lucide-react';

const features = [
  {
    icon: AlertTriangle,
    title: 'Underpricing Detection',
    description: 'Instantly see if your bid price leaves enough margin to absorb real-world cost variance.'
  },
  {
    icon: Calculator,
    title: 'Margin & Downside Exposure',
    description: 'Know your exact margin position and what happens if costs overrun by 10%.'
  },
  {
    icon: TrendingUp,
    title: 'Recommended Adjustment',
    description: 'Get a specific £-amount and %-increase to move your bid into the safe margin band.'
  },
  {
    icon: Shield,
    title: 'Before / After Scenarios',
    description: 'See the commercial impact of changing your price — before you submit.'
  },
  {
    icon: Upload,
    title: 'Deeper Analysis',
    description: 'Create an account to run detailed breakdowns, track bids, and download PDF reports.'
  },
  {
    icon: FileText,
    title: 'Director-Ready Reports',
    description: 'Generate professional bid analysis reports for internal review and board discussions.'
  }
];

const stats = [
  { value: '3', label: 'Fields to check a bid' },
  { value: '30s', label: 'To get your answer' },
  { value: '15%', label: 'Margin improvement potential' },
];

const workflowSteps = [
  {
    step: '01',
    title: 'Enter three numbers',
    desc: 'Contract value, estimated cost, and competition level. That\'s it. No sign-up required.',
  },
  {
    step: '02',
    title: 'Get a clear decision',
    desc: 'BidSentry tells you if you\'re underpricing, by how much, and exactly what to change.',
  },
  {
    step: '03',
    title: 'Submit with confidence',
    desc: 'Adjust your bid — or confirm it\'s sound — before you press send.',
  },
];

export default function LandingPage() {
  return (
    <MarketingLayout>
      {/* Hero Section — Industry Positioning */}
      <section className="relative min-h-[90vh] flex items-center bg-blueprint overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/95 to-slate-50/90" />

        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-sm text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Pre-Submission Commercial Decision Check
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-4 uppercase"
                style={{ fontFamily: 'Barlow Condensed' }}
                data-testid="hero-headline"
              >
                Stop Unknowingly<br />
                <span className="text-orange-500">Underpricing Your Bids</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-lg leading-relaxed">
                Upload your ITT or enter three numbers.
                Know if you're leaving money on the table — in seconds, not hours.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/check">
                  <Button
                    className="btn-primary text-lg px-8"
                    data-testid="hero-cta-btn"
                  >
                    Analyse Your Contract
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="btn-secondary text-lg">
                    View Pricing
                  </Button>
                </Link>
              </div>

              <p className="text-xs text-slate-400 mt-4" data-testid="hero-micro-trust">
                Analyse your first contract free — no sign-up, no card.
              </p>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative bg-white rounded-sm shadow-2xl border border-slate-200 p-6">
                <div className="text-sm text-slate-500 mb-4 uppercase tracking-wider">Bid Check Result</div>

                <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4">
                  <p className="text-xs font-bold text-red-800 uppercase tracking-wider">Decision</p>
                  <p className="text-base font-bold text-red-700 mt-1">You are likely underpricing this bid</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-slate-50 rounded-sm p-3">
                    <div className="text-xl font-mono font-bold text-orange-500">8.2%</div>
                    <div className="text-[10px] text-slate-500 uppercase">Current Margin</div>
                  </div>
                  <div className="bg-slate-50 rounded-sm p-3">
                    <div className="text-xl font-mono font-bold text-orange-500">+£18,500</div>
                    <div className="text-[10px] text-slate-500 uppercase">Recommended Increase</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span>10% overrun = loss-making</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>If adjusted: margin stabilises at 14%</span>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-500/10 rounded-sm" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-slate-900/5 rounded-sm" />
            </div>
          </div>
        </div>
      </section>

      {/* Example Director Report Section */}
      <section className="py-20 lg:py-28 bg-white border-t border-slate-200" data-testid="example-report-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-8 uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="director-report-headline"
          >
            Example Director Report
          </h2>

          <p className="text-base lg:text-lg text-slate-600 leading-relaxed mb-10">
            Most subcontractors don't need another dashboard. They need clarity before
            submitting a bid. Here's a real example of a BidSentry Director Report generated
            from a UK electrical subcontractor's live tender data.
          </p>

          <div className="mb-10">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">
              What the Report Shows
            </h3>
            <ul className="space-y-2.5">
              {[
                'Gross margin estimate',
                'Bid Health Score (0\u2013100)',
                'Competitive position: Too Aggressive / Balanced / Overpriced',
                'Sensitivity to labour and materials cost shifts',
                'Underpricing risk flags',
                'Clear margin band recommendation',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm lg:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center mb-16">
            <a
              href="/example-report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-example-report-btn"
            >
              <Button className="btn-primary text-base px-10">
                View Example Report
                <FileText className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <p className="text-xs text-slate-400 mt-3">No sign-up required.</p>
          </div>

          <div className="border-t border-slate-200 pt-12">
            <h3
              className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              WHAT THIS MEANS IN PRACTICE
            </h3>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed mb-10">
              In the example report, one project shows a Bid Health Score of 52 &mdash; it
              was won, but margin was almost wiped out after small materials variance.
              That's the type of risk most firms don't see until it's too late. BidSentry
              flags it before submission.
            </p>

            <div className="text-center">
              <Link to="/audit/free-bid-audit" data-testid="run-free-audit-btn">
                <Button className="btn-primary text-base px-10">
                  Run Free Bid Margin Audit
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <p className="text-xs text-slate-400 mt-3">No software to install. No obligation. Turnaround: 24&ndash;48 hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Deterministic Model Statement */}
      <section className="py-12 lg:py-16 bg-white border-t border-slate-200" data-testid="deterministic-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 border border-slate-200 rounded-sm mb-5">
            <Cpu className="w-6 h-6 text-slate-700" strokeWidth={1.5} />
          </div>
          <p
            className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="deterministic-statement"
          >
            Built on a deterministic financial model — not AI guesswork.
          </p>
          <p className="text-sm text-slate-500 mt-3 max-w-md mx-auto leading-relaxed">
            Every number in your report is calculated from your actual cost data
            using fixed financial logic. No probabilistic hallucinations. Fully auditable.
          </p>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 lg:py-20 bg-slate-50 border-t border-slate-200" data-testid="trust-section">
        <div className="max-w-5xl mx-auto px-4">
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 text-center mb-12"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            BUILT FOR REAL-WORLD TENDER DECISIONS
          </h2>

          <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
            {[
              {
                icon: FileBarChart,
                title: 'Director-Ready Reports',
                text: 'Clean, printable PDF reports suitable for internal review and board discussions.',
              },
              {
                icon: MapPin,
                title: 'UK Subcontractor Focused',
                text: 'Designed specifically for UK electrical and specialist subcontractors pricing competitive tenders.',
              },
              {
                icon: Lock,
                title: 'Data Stays Private',
                text: 'Your bid data is isolated to your organisation and never shared without consent.',
              },
            ].map((col, i) => (
              <div key={i} className="text-center md:text-left">
                <div className="inline-flex items-center justify-center w-10 h-10 border border-slate-300 rounded-sm mb-4">
                  <col.icon className="w-5 h-5 text-slate-700" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1.5">{col.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{col.text}</p>
              </div>
            ))}
          </div>

          <hr className="border-slate-200 mt-12 mb-6" />
          <p className="text-center text-xs text-slate-400 tracking-wide" data-testid="trust-bar">
            Registered UK Company&ensp;&middot;&ensp;Stripe Secure Payments&ensp;&middot;&ensp;No Long-Term Contracts
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, i) => (
              <div key={i} className="animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="text-5xl font-mono font-bold text-orange-500 mb-2">{stat.value}</div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-600 mt-8">Based on demo dataset modelling.</p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              WHAT YOU GET IN SECONDS
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              A clear commercial decision — not another dashboard to learn.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="card-industrial p-8 animate-slide-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-orange-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-Step Workflow */}
      <section className="py-24 bg-slate-50 bg-blueprint" data-testid="workflow-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
              HOW IT WORKS
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-xl mx-auto">
              From raw cost data to a director-ready report in three steps.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {workflowSteps.map((item, i) => (
              <div key={i} className="relative">
                <div className="text-8xl font-black text-slate-200 absolute -top-4 left-0" style={{ fontFamily: 'Barlow Condensed' }}>
                  {item.step}
                </div>
                <div className="relative pt-16 pl-4">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
            ABOUT TO SUBMIT?<br />
            <span className="text-orange-500">CHECK YOUR PRICE FIRST.</span>
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
            Upload your tender document or enter three numbers. Instant answer. No sign-up required.
          </p>
          <Link to="/check">
            <Button className="btn-primary text-lg px-10" data-testid="cta-start-btn">
              Analyse Your Contract
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-xs text-slate-500 mt-4" data-testid="cta-micro-trust">
            Your first contract check is free.
          </p>
        </div>
      </section>

      {/* Footer Trust Line */}
      <section className="py-4 bg-slate-950 border-t border-slate-800" data-testid="footer-trust-section">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[11px] text-slate-500 tracking-wide" data-testid="footer-trust-line">
            Designed for UK public sector tendering.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
